import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useGlows } from '../hooks/useGlows'
import { useMe } from '../hooks/useMe'
import { useBulkMoveGlows } from '../hooks/useBulkMoveGlows'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { useLongPress } from '../hooks/useLongPress'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Spinner } from '../components/ui/Spinner'
import { Badge } from '../components/Badge'
import { ErrorBanner } from '../components/ErrorBanner'
import { Modal } from '../components/ui/Modal'
import { CategoryCombobox } from '../components/ui/CategoryCombobox'
import { mergeCategorySuggestions } from '../lib/categories'
import type { Glow } from '../api/types'
import { glowHost } from '../lib/glowUrl'

// localStorage key for the per-group collapse state — an array of the
// COLLAPSED category names. A category absent from the array is expanded
// (so new/unknown groups default expanded, and a category never seen
// before never persists as collapsed). Uncategorized uses the reserved
// "" sentinel as its collapse key (its category value IS "").
const COLLAPSED_KEY = 'backlit.glowlist.collapsed'

interface GlowGroupData {
  category: string // "" === Uncategorized
  glows: Glow[]
}

// groupGlowsByCategory groups glows by their EXACT category value (no case
// canonicalization — "Work" and "work" stay separate groups). In-use
// categories sort alphabetical (case-insensitive, locale-aware); Uncategorized
// (key "") is always LAST. Empty default groups are NOT emitted (only
// categories with at least one glow appear). Coalesces a missing/undefined
// category into "" so a rolling-deploy gap (a glow doc decoded before the field
// shipped) can't crash the page. Pure — no I/O, no React, mutates no argument.
function groupGlowsByCategory(glows: Glow[]): GlowGroupData[] {
  const byCat = new Map<string, Glow[]>()
  for (const g of glows) {
    const key = g.category || ''
    const bucket = byCat.get(key)
    if (bucket) bucket.push(g)
    else byCat.set(key, [g])
  }
  const named = [...byCat.keys()].filter((k) => k !== '')
  named.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
  const groups: GlowGroupData[] = named.map((category) => ({
    category,
    glows: byCat.get(category)!,
  }))
  const uncategorized = byCat.get('')
  if (uncategorized) groups.push({ category: '', glows: uncategorized })
  return groups
}

export function GlowList() {
  const { data: glows, isLoading, error } = useGlows()
  const me = useMe()
  const bulkMove = useBulkMoveGlows()

  // 0 == unlimited (mirrors the UsageTab cap convention). Gate the
  // primary "New glow" affordance once the owner is at their limit; the
  // server is still the hard boundary (POST returns 402
  // glow_limit_reached). Don't block while useMe / useGlows load — fall
  // through to the create-page 402 if we let an over-limit click slip.
  const maxGlows = me.data?.subscription.max_glows ?? 0
  const atLimit = maxGlows > 0 && (glows?.length ?? 0) >= maxGlows

  // Any glow auto-darkened by a plan downgrade (status Dark + dark_reason
  // 'downgrade'). Drives a one-line account-level notice so the owner sees
  // WHY some cards carry the red "over limit" badge without opening each one.
  const hasDowngradeDark = (glows ?? []).some(
    (g) => g.status === 'Dark' && g.dark_reason === 'downgrade',
  )

  // Client-side grouping over the already-fetched glows — no new query.
  const groups = useMemo(() => groupGlowsByCategory(glows ?? []), [glows])

  // Per-group collapse state (array of collapsed category names; "" is the
  // Uncategorized sentinel). New/unknown groups default expanded; stale names
  // harmlessly match nothing.
  const [collapsed, setCollapsed] = useLocalStorage<string[]>(COLLAPSED_KEY, [])
  function toggleCollapsed(key: string) {
    setCollapsed(
      collapsed.includes(key)
        ? collapsed.filter((c) => c !== key)
        : [...collapsed, key],
    )
  }

  // Selection is the single source of truth: "select mode" is simply whether
  // anything is selected. A long-press on a card (touch or mouse) enters select
  // mode by selecting that card; deselecting everything (or Clear) exits it — so
  // there is no separate Select/Cancel button.
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const selectMode = selected.size > 0
  const [moveOpen, setMoveOpen] = useState(false)
  const [moveTarget, setMoveTarget] = useState('')
  const [moveError, setMoveError] = useState('')

  // Long-press adds (selects) a card and thereby enters select mode. Idempotent:
  // long-pressing an already-selected card leaves it selected.
  function addSelected(id: string) {
    setSelected((prev) => (prev.has(id) ? prev : new Set(prev).add(id)))
  }
  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function selectGroup(group: GlowGroupData, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev)
      for (const g of group.glows) {
        if (on) next.add(g.id)
        else next.delete(g.id)
      }
      return next
    })
  }
  function clearSelection() {
    setSelected(new Set())
  }

  function openMove() {
    setMoveTarget('')
    setMoveError('')
    setMoveOpen(true)
  }

  async function confirmMove() {
    setMoveError('')
    const ids = [...selected]
    const res = await bulkMove.mutateAsync({ ids, category: moveTarget.trim() })
    if (res.failed.length === 0) {
      setSelected(new Set())
      setMoveOpen(false)
    } else {
      // Keep only the failed ids selected so a retry re-targets just them.
      setSelected(new Set(res.failed.map((f) => f.id)))
      setMoveError(
        `Moved ${res.succeeded.length} of ${
          res.succeeded.length + res.failed.length
        }; ${res.failed.length} failed — try again`,
      )
      // Modal stays open for the retry.
    }
  }

  const suggestions = useMemo(
    () =>
      mergeCategorySuggestions(
        // A glow from an older console-api can lack `category` (undefined), so
        // narrow to non-empty strings — a bare `!== ''` would let undefined
        // through and crash mergeCategorySuggestions.
        (glows ?? []).map((g) => g.category).filter((c): c is string => !!c),
      ),
    [glows],
  )

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Your glows</h1>
          <p className="text-sm text-cream/70">
            Static apps you&apos;ve deployed to backlit.run.
          </p>
        </div>
        {atLimit ? (
          <div className="sm:text-right">
            <Button disabled title="You've reached your glow limit.">
              New glow
            </Button>
            <p className="mt-1 text-xs text-muted">
              You&apos;ve reached your limit of {maxGlows}{' '}
              {maxGlows === 1 ? 'glow' : 'glows'}.{' '}
              <Link to="/billing" className="font-medium text-glow-600 hover:underline">
                Upgrade
              </Link>{' '}
              for more.
            </p>
          </div>
        ) : (
          <Link to="/glows/new">
            <Button>New glow</Button>
          </Link>
        )}
      </div>

      {hasDowngradeDark && (
        <div className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          Some glows were darkened because you&apos;re over your plan&apos;s glow
          limit. Relight one after darkening or deleting another — or{' '}
          <Link to="/billing" className="font-medium underline hover:no-underline">
            upgrade
          </Link>{' '}
          for more lit glows.
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}

      {error && <ErrorBanner>{(error as Error).message}</ErrorBanner>}

      {glows && glows.length === 0 && (
        <Card className="text-center">
          <h2 className="text-lg font-medium">No glows yet</h2>
          <p className="mt-1 text-sm text-cream/70">
            Create your first glow to get started — you&apos;ll get a free{' '}
            <code className="rounded bg-surface-2 px-1 py-0.5 text-xs">*.backlit.run</code>{' '}
            subdomain and an API key for deploying assets.
          </p>
          <Link to="/glows/new" className="mt-4 inline-block">
            <Button>Create your first glow</Button>
          </Link>
        </Card>
      )}

      {glows && glows.length > 0 && (
        // Constant bottom padding so the floating selection footer never covers
        // the last row when scrolled to the bottom. Always-on (not gated on
        // select mode) so it doesn't reintroduce layout shift.
        <div className="space-y-6 pb-24">
          {/* Always rendered (not gated on selectMode) so entering select mode
              doesn't reflow the grid and make glows jump. */}
          <p className="text-xs text-muted">
            Tip: press and hold a glow — or focus one and press Space — to select
            it, then move selected glows between categories.
          </p>

          {groups.map((group) => (
            <GlowGroup
              key={group.category || ' uncategorized'}
              group={group}
              collapsed={collapsed.includes(group.category)}
              onToggleCollapsed={() => toggleCollapsed(group.category)}
              selectMode={selectMode}
              selected={selected}
              onToggleSelect={toggleSelect}
              onLongSelect={addSelected}
              onSelectGroup={selectGroup}
            />
          ))}
        </div>
      )}

      {selectMode && (
        // Fixed overlay floated at the bottom of the viewport — it sits above
        // the page rather than in flow, so showing/hiding it never reflows the
        // list. The outer wrapper is click-through (pointer-events-none); only
        // the bar itself is interactive.
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
          <div className="pointer-events-auto flex w-full max-w-2xl items-center justify-between gap-3 rounded-xl border border-line-strong bg-surface-2 px-4 py-3 shadow-lg">
            <span className="text-sm text-cream">{selected.size} selected</span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={clearSelection}>
                Clear
              </Button>
              <Button onClick={openMove}>
                Move {selected.size} {selected.size === 1 ? 'glow' : 'glows'} to …
              </Button>
            </div>
          </div>
        </div>
      )}

      {moveOpen && (
        <Modal onClose={() => setMoveOpen(false)} labelledBy="move-glows-heading">
          <h3 id="move-glows-heading" className="text-lg font-medium">
            Move {selected.size} {selected.size === 1 ? 'glow' : 'glows'} to …
          </h3>
          <p className="mt-1 text-sm text-cream/70">
            Pick an existing category or type a new one. Leave it empty to move to
            Uncategorized.
          </p>
          {moveError && (
            <ErrorBanner className="mt-3">{moveError}</ErrorBanner>
          )}
          <div className="mt-4">
            <CategoryCombobox
              value={moveTarget}
              onChange={setMoveTarget}
              suggestions={suggestions}
              placeholder="Category"
              aria-label="Target category"
              disabled={bulkMove.isPending}
            />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setMoveOpen(false)}
              disabled={bulkMove.isPending}
            >
              Cancel
            </Button>
            <Button onClick={confirmMove} disabled={bulkMove.isPending}>
              {bulkMove.isPending ? 'Moving…' : 'Move'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// slugForId builds a DOM-safe id fragment from a category name for the
// aria-controls / body id linkage (avoids spaces / punctuation breaking the id).
function slugForId(category: string): string {
  return category === ''
    ? 'uncategorized'
    : category.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'group'
}

function GlowGroup({
  group,
  collapsed,
  onToggleCollapsed,
  selectMode,
  selected,
  onToggleSelect,
  onLongSelect,
  onSelectGroup,
}: {
  group: GlowGroupData
  collapsed: boolean
  onToggleCollapsed: () => void
  selectMode: boolean
  selected: Set<string>
  onToggleSelect: (id: string) => void
  onLongSelect: (id: string) => void
  onSelectGroup: (group: GlowGroupData, on: boolean) => void
}) {
  const bodyId = `group-${slugForId(group.category)}`
  const label = group.category || 'Uncategorized'
  const allSelected =
    group.glows.length > 0 && group.glows.every((g) => selected.has(g.id))

  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          className="flex items-center gap-2 text-left text-sm font-semibold text-cream hover:text-glow-500"
        >
          <span
            aria-hidden="true"
            className={`inline-block transition-transform ${collapsed ? '' : 'rotate-90'}`}
          >
            ▶
          </span>
          <span className="truncate">{label}</span>
        </button>
        <Badge tone="slate">{group.glows.length}</Badge>
        {selectMode && (
          <button
            type="button"
            onClick={() => onSelectGroup(group, !allSelected)}
            aria-pressed={allSelected}
            className="ml-1 text-xs font-medium text-muted hover:text-cream"
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>

      {!collapsed && (
        <div id={bodyId} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {group.glows.map((g) => (
            <GlowCard
              key={g.id}
              glow={g}
              selectMode={selectMode}
              isSelected={selected.has(g.id)}
              onToggle={onToggleSelect}
              onLongSelect={onLongSelect}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function GlowCard({
  glow,
  selectMode,
  isSelected,
  onToggle,
  onLongSelect,
}: {
  glow: Glow
  selectMode: boolean
  isSelected: boolean
  onToggle: (id: string) => void
  onLongSelect: (id: string) => void
}) {
  // Press-and-hold (touch or mouse) selects the card and enters select mode.
  const longPress = useLongPress(() => onLongSelect(glow.id))

  // The card is ALWAYS a Link (no element swap between modes, so the trailing
  // click after a long-press lands on a handler we control). Click behavior:
  //   - just long-pressed → swallow the click (selection already happened);
  //   - select mode      → toggle this card (no navigation);
  //   - browse mode      → let the Link navigate (modifier-clicks still open
  //                        a new tab).
  const handleClick = (e: React.MouseEvent) => {
    if (longPress.consumeClick()) {
      e.preventDefault()
      return
    }
    if (selectMode) {
      e.preventDefault()
      onToggle(glow.id)
    }
  }

  // Keyboard parity for the pointer long-press: Space selects. It enters select
  // mode by selecting this card when browsing, or toggles it when already
  // selecting — so keyboard-only users reach bulk-move without the gesture.
  // Enter keeps the native link role (navigate when browsing; toggle in select
  // mode via the click Enter synthesizes → handleClick).
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ') {
      e.preventDefault() // a focused link would otherwise scroll the page
      if (selectMode) onToggle(glow.id)
      else onLongSelect(glow.id)
    }
  }

  return (
    <Link
      to={`/glows/${glow.id}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      // In select mode the card acts as a selection toggle, not a link — expose
      // it to assistive tech as a pressed-state button (aria-pressed is only
      // valid on a button role, not a bare link). In browse mode it stays a
      // plain link.
      role={selectMode ? 'button' : undefined}
      aria-pressed={selectMode ? isSelected : undefined}
      className="block select-none rounded-xl touch-manipulation [-webkit-touch-callout:none] focus:outline-none focus-visible:ring-2 focus-visible:ring-glow-500/60"
      {...longPress.handlers}
    >
      <Card
        className={`relative h-full transition-colors ${
          selectMode && isSelected
            ? 'border-glow-500 ring-2 ring-glow-500/40'
            : 'hover:border-glow-500/40'
        }`}
      >
      {selectMode && isSelected && (
        <span
          aria-hidden="true"
          className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-glow-600 text-xs font-bold text-ink"
        >
          ✓
        </span>
      )}
      <h3 className="truncate font-semibold">{glow.title || glow.name}</h3>
      <p className="mt-0.5 truncate text-xs text-muted">
        {glowHost(glow.name)}
      </p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Badge tone={glow.status === 'Lit' ? 'amber' : 'slate'}>{glow.status}</Badge>
        {glow.status === 'Dark' && glow.dark_reason === 'downgrade' && (
          <Badge tone="red">Darkened — over limit</Badge>
        )}
        <Badge
          tone={
            glow.auth_mode === 'Public'
              ? 'blue'
              : glow.auth_mode === 'Accounts'
                ? 'green'
                : 'slate'
          }
        >
          {glow.auth_mode}
        </Badge>
        <Badge tone="slate">{glow.region}</Badge>
      </div>
      <p className="mt-3 text-xs text-muted">
        {glow.live_version ? (
          <>Live: <code className="font-mono">{shortId(glow.live_version)}</code></>
        ) : (
          'No deploys yet'
        )}
      </p>
      </Card>
    </Link>
  )
}

function shortId(s: string): string {
  return s.length > 8 ? s.slice(0, 8) : s
}
